import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { appointmentsAPI } from '../api';
import toast from 'react-hot-toast';
import { ArrowLeft, Clock } from 'lucide-react';

const StatusBadge = ({ status }) => {
    const map = {
        Accepted: 'badge-success',
        Completed: 'badge-success',
        Pending: 'badge-warning',
        Assigned: 'badge-info',
        Cancelled: 'badge-danger',
        Timeout: 'badge-danger',
    };
    return (
        <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>
    );
};

export default function AppointmentDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        appointmentsAPI
            .getById(id)
            .then((res) => setData(res.data.data))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, [id]);

    const handleStatusChange = async (newStatus) => {
        try {
            await appointmentsAPI.updateStatus(id, newStatus);
            toast.success('Status updated');
            load();
        } catch {
            toast.error('Failed to update');
        }
    };

    if (loading)
        return (
            <div className="loading-container">
                <div className="spinner" />
            </div>
        );
    if (!data) return <div>Appointment not found</div>;

    return (
        <div className="fade-in">
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 28,
                }}
            >
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/appointments')}
                >
                    <ArrowLeft size={14} /> Back
                </button>
                <h1 className="page-title" style={{ fontSize: 22 }}>
                    Appointment #{data.appointment_id}
                </h1>
                <div
                    style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    <StatusBadge status={data.status} />
                    <select
                        className="input"
                        style={{ width: 'auto' }}
                        value={data.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                    >
                        <option value="Pending">Pending</option>
                        <option value="Assigned">Assigned</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                </div>
            </div>

            <div className="grid-2">
                {/* Appointment Info */}
                <div className="card">
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text1)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                        }}
                    >
                        Appointment Info
                    </h3>
                    {[
                        ['Visit Type', data.appointment_type],
                        ['Purpose', data.consultation_purpose],
                        ['Date', data.appointment_date],
                        ['Time', data.appointment_time],
                        ['Patient Address', data.patient_address],
                        ['Expires At', data.assignment_expires_at],
                        ['Round', data.assignment_round],
                    ].map(([label, val]) => (
                        <div className="detail-row" key={label}>
                            <span className="detail-label">{label}</span>
                            <span className="detail-value">{val || '—'}</span>
                        </div>
                    ))}
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20,
                    }}
                >
                    {/* Patient */}
                    <div className="card">
                        <h3
                            style={{
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            Patient
                        </h3>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                marginBottom: 14,
                            }}
                        >
                            <div
                                style={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 12,
                                    background:
                                        'linear-gradient(135deg, #6366f1, #a78bfa)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: 'white',
                                }}
                            >
                                {(data.patient_name || 'P')[0]}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>
                                    {data.patient_name}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--text3)',
                                    }}
                                >
                                    {data.patient_email}
                                </div>
                            </div>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                            {data.patient_phone}
                        </div>
                    </div>

                    {/* Doctor */}
                    <div className="card">
                        <h3
                            style={{
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            Doctor
                        </h3>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                marginBottom: 14,
                            }}
                        >
                            <div
                                style={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 12,
                                    background:
                                        'linear-gradient(135deg, var(--accent), var(--accent2))',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: 'white',
                                }}
                            >
                                {(data.doctor_name || 'D')[0]}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>
                                    {data.doctor_name || 'Not assigned'}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--text3)',
                                    }}
                                >
                                    {data.specialization}
                                </div>
                            </div>
                        </div>
                        {data.doctor_email && (
                            <div
                                style={{ fontSize: 13, color: 'var(--text2)' }}
                            >
                                {data.doctor_email}
                            </div>
                        )}
                    </div>
                </div>

                {/* Assignment History */}
                {data.history?.length > 0 && (
                    <div className="card" style={{ gridColumn: '1 / -1' }}>
                        <h3
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            <Clock size={15} /> Assignment History
                        </h3>
                        <div style={{ position: 'relative', paddingLeft: 20 }}>
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 7,
                                    top: 8,
                                    bottom: 8,
                                    width: 2,
                                    background: 'var(--border)',
                                }}
                            />
                            {data.history.map((h, i) => (
                                <div
                                    key={h.history_id}
                                    style={{
                                        position: 'relative',
                                        marginBottom: 20,
                                        paddingLeft: 20,
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: -17,
                                            top: 4,
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            border: '2px solid',
                                            borderColor:
                                                h.status === 'Accepted'
                                                    ? 'var(--success)'
                                                    : h.status === 'Timeout'
                                                      ? 'var(--danger)'
                                                      : 'var(--accent)',
                                            background: 'var(--bg)',
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                        }}
                                    >
                                        <strong style={{ fontSize: 14 }}>
                                            {h.doctor_name ||
                                                `Doctor #${h.doctor_id}`}
                                        </strong>
                                        <StatusBadge status={h.status} />
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: 'var(--text3)',
                                            marginTop: 3,
                                        }}
                                    >
                                        Assigned:{' '}
                                        {new Date(
                                            h.assigned_at
                                        ).toLocaleString()}
                                        {h.responded_at &&
                                            ` • Responded: ${new Date(h.responded_at).toLocaleString()}`}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Checkup */}
                {data.checkup && (
                    <div className="card" style={{ gridColumn: '1 / -1' }}>
                        <h3
                            style={{
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            Checkup / Prescription
                        </h3>
                        <p
                            style={{
                                fontSize: 14,
                                color: 'var(--text2)',
                                lineHeight: 1.7,
                            }}
                        >
                            {data.checkup.prescription ||
                                'No prescription recorded'}
                        </p>
                        {data.reports?.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <h4
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--text3)',
                                        marginBottom: 10,
                                    }}
                                >
                                    Reports ({data.reports.length})
                                </h4>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {data.reports.map((r) => (
                                        <a
                                            key={r.report_id}
                                            href={`/uploads/${r.checkup_file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-ghost btn-sm"
                                        >
                                            Report #{r.report_id}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
